<!-- eslint-disable vue/no-static-inline-styles -->
<script lang="ts" setup>
import {
  ref, computed, watch,
} from 'vue';
import { useResizeObserver, useElementSize } from '@vueuse/core'
import ConnxForm from '@/components/Molecules/Field/ConnxForm.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import { InputType } from '@/interfaces/InputFields';
import {
  Grid,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-vue-grid';
import { downloadCSV, downloadExcel } from '@/lib/downloads';
import useHRAdminWorkforceAssistanceStore from '@/stores/HRAdmin/workforce-assistance';
import useApplicationStore from '@/stores/application';
import slugify from '@/lib/slugify';

const workforceAssistanceStore = useHRAdminWorkforceAssistanceStore();
const applicationStore = useApplicationStore();

await workforceAssistanceStore.init();

const reportSettings = workforceAssistanceStore.reportSettings;
const take = computed(() => reportSettings.pageSize);
const skip = ref(0);
const payrollDatabaseIdEl = ref();
const templateContainerEl = ref<HTMLElement | undefined>();
const filterContainerEl = ref();
const filterContainerSizing = useElementSize(filterContainerEl);
const formReport = ref();
const gridWidth = ref();
const firstLoad = ref(true);
const exportDisclaimerOpen = ref(false);
const hideExportDisclaimer = ref(false);
const attemptingToExport = ref('');
const columns = computed(() => workforceAssistanceStore.columnHeadings);
const items = computed(() => workforceAssistanceStore.response.results);
const total = computed(() => workforceAssistanceStore.response.total);

const gridHeight = computed(() => {
  const padding = 140;
  const headerHeight = 100;
  const filterContainerHeight = filterContainerSizing.height.value;

  // Get height of window max height, sub the header navigation height, then sub the filter height
  const value = document.body.clientHeight - headerHeight - filterContainerHeight - padding;

  return value <= 350 ? 350 : value;
})

const noResults = computed(() => {
  return columns.value.length === 1 && columns.value[0].width === 500;
});

const setGridWidth = () => {
  if (noResults.value) {
    return '100%';
  }
  const gridColumnWidths = columns.value.reduce((carry, { width }) => {
    return carry + Number(width);
  }, 20)
  if (gridColumnWidths >= window.innerWidth) {
    gridWidth.value = '100%';
  } else {
    gridWidth.value = `${gridColumnWidths}px`;
  }
}

const fileName = computed(() => [
  'workforce-assistance',
  slugify(reportTypes.find(r => r.value === reportSettings.reportTypeId)?.label!),
  slugify(Object.values(payrollCompanies.value).find(p => p.value === reportSettings.payrollCompanyId)?.label!),
  new Date().valueOf()
].join('-'));

const pageable = {
  buttonCount: 5,
  info: true,
  type: 'numeric',
  previousNext: true
}

const sortChangeHandler = async (event: GridSortChangeEvent) => {
  workforceAssistanceStore.sort = event.sort;
  await updateFilters();
}

const pageChangeHandler = async (event: GridPageChangeEvent) => {
  applicationStore.setLoading(true);
  reportSettings.pageSize = event.page.take;
  skip.value = event.page.skip;
  reportSettings.page = (event.page.skip / event.page.take) + 1;

  await workforceAssistanceStore.getReport();
  const gridContainer = document.querySelector('.k-grid-content');
  if (gridContainer) {
    gridContainer.scrollTop = 0;
    gridContainer.scrollLeft = 0;
  }
  applicationStore.setLoading(false);
}

const reportTypes = workforceAssistanceStore.reportTypes.map((report) => ({
  label: report.description,
  value: report.id,
}));

const payrollDatabases = computed(() => [{ label: '[All]', value: '' }, ...workforceAssistanceStore.payrollDatabases.map((payrollDatabase) => ({
  label: payrollDatabase.companyName,
  value: payrollDatabase.id,
}))])

const payrollCompanies = computed(() => [{ label: '[All]', value: '' }, ...workforceAssistanceStore.payrollCompanies.map((payrollCompany) => ({
  label: payrollCompany.payerName,
  value: payrollCompany.payerId,
}))]);

const payrollCompanySelectVisible = computed(() => {
  return (reportSettings.payrollDatabaseId && payrollCompanies.value.length > 1)
});

const updateFilters = async () => {
  if (applicationStore.loading) return;

  applicationStore.setLoading(true);
  reportSettings.page = 1;
  await workforceAssistanceStore.getReport();
  firstLoad.value = false;

  applicationStore.setLoading(false);
}

const exportCSV = async (acceptedDisclaimer: boolean = false): Promise<void> => {
  attemptingToExport.value = 'csv';
  if (workforceAssistanceStore.hideExportDisclaimer || acceptedDisclaimer) {
    workforceAssistanceStore.exporting = true;
    const data = await workforceAssistanceStore.getReport();
    workforceAssistanceStore.exporting = false;

    downloadCSV(data.results, fileName.value);

    return;
  }
  exportDisclaimerOpen.value = true;

}

const exportExcel = async (acceptedDisclaimer: boolean = false): Promise<void> => {
  attemptingToExport.value = 'excel';
  if (workforceAssistanceStore.hideExportDisclaimer || acceptedDisclaimer) {
    workforceAssistanceStore.exporting = true;
    const data = await workforceAssistanceStore.getReport();
    workforceAssistanceStore.exporting = false;

    downloadExcel(data.results, workforceAssistanceStore.columnHeadings, fileName.value);
    return;
  }
  exportDisclaimerOpen.value = true;
}

const disclaimerAccepted = () => {
  workforceAssistanceStore.hideExportDisclaimer = hideExportDisclaimer.value;
  exportDisclaimerOpen.value = false;
  switch (attemptingToExport.value) {
    case ('csv'):
      exportCSV(true);
      break;
    case ('excel'):
      exportExcel(true);
      break;
  }
}

const dateLabelMap = new Map([
  [1, { start: 'Effective Date From:', end: 'Effective Date To:' }],
  [2, { start: 'Termination Date From:', end: 'Termination Date To:' }],
  [3, { start: 'Date Hired From:', end: 'Date Hired To:' }],
  [4, { start: 'Leave Start Date From:', end: 'Leave Start Date To:' }],
]);

const dateLabels = computed(() => {
  if (dateLabelMap.has(reportSettings.reportTypeId)) {
    return dateLabelMap.get(reportSettings.reportTypeId)
  }
  return { start: undefined, end: undefined }
})

const canShowGrid = computed(() => {
  return !firstLoad || !noResults.value;
})

watch(() => reportSettings.reportTypeId, async (value) => {
  // Clearing set values that will need to be updated once report change
  workforceAssistanceStore.payrollCompanies = [];
  workforceAssistanceStore.sort = [];
  reportSettings.payrollDatabaseId = '';
});

watch(() => reportSettings.payrollDatabaseId, async (value) => {
  workforceAssistanceStore.payrollCompanies = [];
  workforceAssistanceStore.sort = [];
  applicationStore.setLoading(true);
  reportSettings.payrollCompanyId = '';
  await workforceAssistanceStore.getPayrollCompanies(Number(value));
  applicationStore.setLoading(false);
});

useResizeObserver(templateContainerEl, () => {
  setGridWidth();
});

watch(() => workforceAssistanceStore.columnHeadings, () => {
  setGridWidth();
})
</script>

<template>
  <connx-confirmation-modal
    :open="exportDisclaimerOpen"
    heading-text="Disclaimer"
    size="medium"
  >
    <p>These Workforce Assistance Reports are provided “as is”. ConnX Pty Ltd makes no representations or warranties of
      any kind, express or implied, as to the accuracy or completeness of the data generated or whether it will meet
      your requirements for use in any capacity whatsoever.</p>
    <p>By generating and using a Workforce Assistance Report you acknowledge that you use and rely on the data contained
      in the reports at your own risk and that ConnX Pty Ltd its directors, officers, employees, servants, authorised
      distributors and agents have no responsibility or liability to you in any way whatsoever.</p>
    <connx-field
      name="confirm"
      class="tw-mb-0"
      :type="InputType.Checkbox"
      v-model="hideExportDisclaimer"
    >Don't show this message again.</connx-field>
    <template #footer>
      <connx-button
        class="tw-min-w-32"
        button-style="success"
        @click="disclaimerAccepted"
      >
        I Agree
      </connx-button>
      <connx-button
        class="tw-min-w-32"
        @click="() => exportDisclaimerOpen = false"
      >
        Cancel
      </connx-button>
    </template>
  </connx-confirmation-modal>
  <div
    class="tw-flex tw-flex-col tw-h-full"
    ref="templateContainerEl"
  >
    <div ref="filterContainerEl">
      <connx-form
        ref="formReport"
        class="tw-order-2 md:tw-order-2"
      >
        <div class="
          tw-grid
          tw-auto-cols-max
          tw-gap-4
          tw-grid-cols-[auto,auto,auto,auto,1fr]
        ">
          <label
            for="report_type"
            class="tw-flex tw-items-center tw-m-0"
          >
            Report Type:
          </label>
          <div class="tw-row-start-1 tw-col-start-2">
            <connx-field
              v-model="reportSettings.reportTypeId"
              :type="InputType.Select"
              :inline="true"
              class="tw-mb-0"
              id="report_type"
              name="report_type"
              :options="reportTypes"
            >
            </connx-field>
          </div>
          <label
            for="payroll_db"
            class="tw-row-start-2 tw-flex tw-items-center tw-m-0"
          >Payroll Database:</label>
          <div class="tw-row-start-2">
            <connx-field
              ref="payrollDatabaseIdEl"
              class="tw-mb-0"
              :type="InputType.Select"
              name="payroll_db"
              v-model="reportSettings.payrollDatabaseId"
              :options="payrollDatabases"
            />
          </div>
          <label
            for="payroll_company"
            v-if="payrollCompanySelectVisible"
            class="tw-row-start-2 tw-flex tw-items-center tw-m-0"
          >Payroll Company:</label>
          <div
            class="tw-row-start-2  tw-flex tw-items-center tw-m-0"
            v-if="payrollCompanySelectVisible"
          >
            <connx-field
              class="tw-mb-0"
              :type="InputType.Select"
              name="payroll_company"
              v-model="reportSettings.payrollCompanyId"
              :options="payrollCompanies"
            />
          </div>
          <label
            v-if="reportSettings.reportTypeId <= 4"
            for="date-from"
            class="tw-row-start-3 tw-col-start-1 tw-flex tw-items-center tw-m-0"
          >{{ dateLabels?.start }}</label>
          <ConnxField
            :type="InputType.Date"
            name="date-from"
            id="date-from"
            v-if="reportSettings.reportTypeId <= 4"
            v-model="reportSettings.effectiveFromDate"
            class="tw-row-start-3 tw-col-start-2"
          />
          <label
            v-if="reportSettings.reportTypeId <= 4"
            for="date-to"
            class="tw-row-start-3 tw-col-start-3 tw-flex tw-items-center tw-m-0"
          >{{ dateLabels?.end }}</label>
          <ConnxField
            :type="InputType.Date"
            name="date-to"
            id="date-to"
            v-if="reportSettings.reportTypeId <= 4"
            v-model="reportSettings.effectiveToDate"
            class="tw-row-start-3 tw-col-start-4"
          />
          <div
            :class="{
              'desktop:tw-col-start-5': reportSettings.reportTypeId <= 4,
            }"
            class="tw-col-span-5 tw-row-start-4 desktop:tw-row-start-3"
          >
            <div class="tw-flex tw-items-center tw-justify-between">
              <connx-button
                @click="updateFilters()"
                type="submit"
                :disabled="applicationStore.loading"
                button-style="brand-primary"
              >
                Generate
              </connx-button>
              <div
                class="tw-flex tw-gap-x-2"
                v-show="canShowGrid"
              >
                <connx-button
                  button-style="white"
                  icon-class="tw-text-connx-grey"
                  title="Export to Excel"
                  @click="exportExcel()"
                  icon-position="before"
                  icon="fas fa-cloud-download"
                >
                  Export to Excel
                </connx-button>
                <connx-button
                  button-style="white"
                  icon-class="tw-text-connx-grey"
                  title="Export to CSV"
                  @click="exportCSV()"
                  icon-position="before"
                  icon="fas fa-cloud-download"
                >
                  Export to CSV
                </connx-button>
              </div>
            </div>
          </div>
        </div>
      </connx-form>
    </div>
    <div
      class="tw-grow tw-min-h-[350px]"
      :class="{
        'tw-flex tw-justify-center tw-items-center': !firstLoad && noResults,
      }"
    >
      <grid
        v-if="canShowGrid"
        :pageable="pageable"
        :class="{
          'k-no-results': noResults,
        }"
        class="tw-mt-2"
        :style="{ width: gridWidth, height: `${gridHeight}px` }"
        :data-items="items"
        scrollable="scrollable"
        :take="take"
        :total="total"
        :skip="skip"
        :row-class="() => 'tw-group'"
        :sortable="{
          allowUnsort: true,
          mode: 'multiple'
        }"
        :sort="workforceAssistanceStore.sort"
        @sortchange="sortChangeHandler"
        :selectable="false"
        :columns="columns"
        @pagechange="pageChangeHandler"
      >
      </grid>
      <p
        v-if="!firstLoad && noResults"
        class="tw-mt-3"
      >
        There are no records that match your current filter.
      </p>
    </div>
  </div>
</template>
